import { call, put, takeLatest, take } from "redux-saga/effects";
import * as i18n from "util/i18n";
import { store } from "store";
import { selectors as sessionSelectors } from "reducers/session";
import {
    loadSentRequestsMiddleware,
    sendAddAccountsAction,
    cancelDebin,
    loadIncomingDebinListMiddleware,
    removeSellerAccountMiddleware,
    createDebin,
    createCredin,
    createDebinPre,
    createCredinPre,
    acceptIncomingDebinPreMiddleware,
    acceptIncomingDebinSendMiddleware,
    declineIncomingDebinSendMiddleware,
    loadSellerDebinDetail,
    listConcepts,
    getSeller,
    relateBuyerToRecurrency,
    getRecurrencies,
    setUpdateRecurrencyState,
    addNewVendorAccountRequest,
    removeVendorAccountRequest,
    accountInfoByCbuCvuAlias,
    listCredentialsGroupsMidle,
    scheduledDebinListMidle,
    acceptScheduledDebinSendMid,
    rejectScheduledDebinSendMid,
    pauseScheduledDebinSendMid,
    activateScheduledDebinSendMid,
    chargeBackSendMiddleware,
    chargeBackTrxPlSend,
    cancelScheduledDebinSendMid,
} from "middleware/debin";
import { types, actions as debinActions } from "reducers/debin";
import { actions as notificationActions } from "reducers/notification";
import { routerActions, push, replace } from "connected-react-router";
import { actions as formActions } from "reducers/form";
import { INSUFFICIENT_SIGNATURES, TRANSACTION_FINISHED } from "util/responses";
import gaEventTracker, { events } from "util/gaEventTracker";
import { getStateByCode, codeStates, transactionStatus } from "pages/transactions/constants";
import { isEmpty } from "lodash";
import { credentialsErrorMessage } from "util/validateMessage";

const sagas = [
    takeLatest(types.LOAD_SENT_REQUESTS_REQUEST, loadSentRequests),
    takeLatest(types.SEND_ADD_ACCOUNT_ACTION_REQUEST, sendAddAccountAction),
    takeLatest(types.CANCEL_DEBIN_REQUEST, cancelDebinRequest),
    takeLatest(types.LOAD_INCOMING_DEBIN_LIST_REQUEST, loadIncomingDebinList),
    takeLatest(types.REMOVE_SELLER_ACCOUNT_REQUEST, removeSellerAccount),
    takeLatest(types.ACCEPT_INCOMING_DEBIN_PRE_REQUEST, acceptIncomingDebinPreRequest),
    takeLatest(types.ACCEPT_INCOMING_DEBIN_PRE_REQUEST_REDIRECT, acceptIncomingDebinPreRequestRedirect),
    takeLatest(types.DEBIN_SEND_REQUEST, sendDebinCreateRequest),
    takeLatest(types.SEND_DEBIN_PRE_REQUEST, sendDebinCreatePreRequest),
    takeLatest(types.ACCEPT_INCOMING_DEBIN_SEND_REQUEST, acceptIncomingDebinSendRequest),
    takeLatest(types.DECLINE_INCOMING_DEBIN_SEND_REQUEST, declineIncomingDebinSendRequest),
    takeLatest(types.SEND_CREDIN_PRE_REQUEST, sendCredinCreatePreRequest),
    takeLatest(types.SEND_CREDIN_REQUEST, sendCredinCreateRequest),
    takeLatest(types.GET_SELLER_DEBIN_DETAIL_REQUEST, getSellerDebinDetail),
    takeLatest(types.DECLINE_FIXED_TERM_SEND_REQUEST, declineFixedTermSendRequest),
    takeLatest(types.DEBIN_PRE_REQUEST, debinPreRequest),
    takeLatest(types.DEBIN_SELLER_CUIT, getSellerInfo),
    takeLatest(types.RELATE_BUYER_RECURRENCY, relateBuyerRecurrency),
    takeLatest(types.DEBIN_RECURRENCIES, getDebinRecurrencies),
    takeLatest(types.SET_UPDATE_RECURRENCY_STATE, updateRecurrencyState),
    takeLatest(types.SET_IS_BACK_FROM_SUMMARY, setIsBackFromSummary),
    takeLatest(types.ADD_NEW_VENDOR_REQUEST, addNewVendorAccount),
    takeLatest(types.REMOVE_VENDOR_REQUEST, removeVendorAccount),
    takeLatest(types.GET_ACCOUNT_INFO_BY_CBU_CVU_ALIAS, getAccountInfoByCbuCbuCvuAlias),
    takeLatest(types.GET_ACCOUNT_INFO_BY_CBU_CVU_ALIAS_APPLICANT, getAccountInfoByCbuCbuCvuAliasApplicant),
    takeLatest(types.DEBIN_TICKET_CREDENTIAL_REQUEST, listCredentialsGroups),
    takeLatest(types.SCHEDULED_DEBINS_LIST_REQUEST, scheduledDebinList),
    takeLatest(types.REJECT_SCHEDULED_DEBIN_REQUEST, rejectScheduledDebinSend),
    takeLatest(types.ACCEPT_SCHEDULED_DEBIN_REQUEST, acceptScheduledDebinSend),
    takeLatest(types.PAUSE_SCHEDULED_DEBIN_REQUEST, pauseScheduledDebinSend),
    takeLatest(types.CANCEL_SCHEDULED_DEBIN_REQUEST, cancelScheduledDebinSend),
    takeLatest(types.ACTIVATE_SCHEDULED_DEBIN_REQUEST, activateScheduledDebinSend),
    takeLatest(types.CHARGE_BACK_REQUEST, chargeBackSend),
    takeLatest(types.CHARGE_BACK_TRXPL_REQUEST, chargeBackSendTrxPl),
];
const ticketStates = {
    COR020W: "ERROR",
    COR023I: transactionStatus.PENDING,
    COR000I: transactionStatus.FINISHED,
    COR610W: transactionStatus.FAILED,
};

export default sagas;

function* removeVendorAccount({ idAccount }) {
    const response = yield call(removeVendorAccountRequest, idAccount);
    if (response.type === "W") {
        yield put({
            type: types.REMOVE_VENDOR_REQUEST_FAILURE,
        });
    } else {
        yield put(
            notificationActions.showNotification(i18n.get("debin.success.remove"), "success", [
                "removeVendorAccountDebin",
            ]),
        );
        yield put({
            type: types.REMOVE_VENDOR_REQUEST_SUCCESS,
        });
    }
}

function* addNewVendorAccount({ idAccount }) {
    const { userId } = sessionSelectors.getUser(store.getState());
    const params = { ...idAccount, userId };
    const response = yield call(addNewVendorAccountRequest, params);
    if (response.type === "W" && (response.data.code !== "API524W" || response.data.code !== "API521W")) {
        yield put(
            notificationActions.showNotification(
                response.data.data.message || i18n.get("debin.failure.request"),
                "error",
                ["addNewVendorAccountDebin"],
            ),
        );
        yield put({
            type: types.ADD_NEW_VENDOR_REQUEST_FAILURE,
            addVendorAccount: {
                status: "FAILURE",
            },
        });
    } else {
        yield put(
            notificationActions.showNotification(i18n.get("debin.success.request"), "success", [
                "addNewVendorAccountDebin",
            ]),
        );
        yield put({
            type: types.ADD_NEW_VENDOR_REQUEST_SUCCESS,
            addVendorAccount: {
                status: "SUCCESS",
            },
        });
    }
}

function* updateRecurrencyState({ debin, formikBag, credentialType }) {
    const { action } = debin;
    if (action === "PAUSAR") {
        gaEventTracker.triggerEvent(events.debin_pausar_recurrencia());
    } else if (action === "ELIMINAR") {
        gaEventTracker.triggerEvent(events.debin_eliminar_recurrencia());
    }
    const { userId } = sessionSelectors.getUser(store.getState());
    try {
        const response = yield call(setUpdateRecurrencyState, debin, userId);
        const { isFinished, hasValidationError } = getStateByCode(response.data.code);
        if (hasValidationError) {
            const msj = response.data?.data[credentialType];
            if (msj) {
                yield put(notificationActions.showNotification(msj, "error", ["debinActions"]));
                formikBag.setErrors({
                    credential: msj,
                });
                return;
            }
            yield put(notificationActions.showNotification(response.data?.message, "error", ["debinActions"]));
            return;
        }

        yield put({
            type: types.DEBIN_TICKET_SUCCESS,
            debinTicket: {
                status: codeStates[response.data.code],
                ...response.data,
            },
        });

        if (isFinished) {
            if (action === "PAUSAR") {
                yield put(
                    notificationActions.showNotification(i18n.get("debin.recurrencie.paused"), "success", [
                        "debin.actions",
                    ]),
                );
            } else if (action === "ACTIVAR") {
                yield put(
                    notificationActions.showNotification(i18n.get("debin.recurrencie.activate"), "success", [
                        "debin.actions",
                    ]),
                );
            } else {
                yield put(notificationActions.showNotification(response.data.message, "success", ["debin.actions"]));
            }
        }
    } catch (error) {
        yield put(notificationActions.showNotification(error.data.message, "error", ["debinActions"]));
        yield put({
            type: types.CLEAN_DEBIN_RECURRENCIES,
        });
    } finally {
        formikBag.setSubmitting(false);
    }
}

function* getDebinRecurrencies({ formFilter }) {
    const response = yield call(getRecurrencies, formFilter);
    if (response.type === "W") {
        yield put(notificationActions.showNotification(response.data.message, "error", ["debin"]));
        yield put({
            type: types.CLEAN_DEBIN_RECURRENCIES,
        });
    } else if (response.data.data?.error) {
        yield put({
            type: types.CLEAN_DEBIN_RECURRENCIES,
        });
    } else {
        yield put({
            type: types.SET_DEBIN_RECURRENCIES,
            debinRecurrency: response.data.data?.recurrencies,
            // paginas_totales: response.data.data?.paginas_totales,
        });
    }
}

function* relateBuyerRecurrency(props) {
    const { userId } = sessionSelectors.getUser(store.getState());
    const params = { ...props.debinPrestacion, userId };
    const response = yield call(relateBuyerToRecurrency, params);
    try {
        if (response.data.code === "COR020W") {
            const { data } = response.data;
            const message = credentialsErrorMessage(data) || i18n.get("global.unexpectedError");

            yield put(notificationActions.showNotification(message, "error", ["/debinPayments/AddRecurence"]));
        } else if (response.type === "W") {
            if (response.data.code !== "API516W") {
                yield put(notificationActions.showNotification(response.data.message, "error", ["debin"]));
            }

            if (response.data.code === "API516W") {
                yield put(
                    notificationActions.showNotification(response.data?.data?.descripcion, "error", [
                        "/debinPayments/AddRecurence",
                    ]),
                );
            }
        } else {
            if (response.data.code === INSUFFICIENT_SIGNATURES) {
                yield put(
                    notificationActions.showNotification(i18n.get("debin.recurrency.activate.pending"), "success", [
                        "debin.ticket.recurrency",
                    ]),
                );
            } else if (response.data.code === TRANSACTION_FINISHED) {
                yield put(
                    notificationActions.showNotification(i18n.get("debin.recurrency.activate.success"), "success", [
                        "debin.ticket.recurrency",
                    ]),
                );
            }

            yield put({ type: types.CLEAN_DEBIN_PRESTACION });
            yield put({
                type: types.DEBIN_TICKET_SUCCESS,
                debinTicket: {
                    status: ticketStates[response.data.code],
                    ...response.data,
                },
            });
        }
    } finally {
        if (props?.setSubmitting) {
            props.setSubmitting.setSubmitting(false);
        }
    }
}

function* getSellerInfo({ seller, setSubmitting = null }) {
    try {
        const { userId } = sessionSelectors.getUser(store.getState());
        const response = yield call(getSeller, seller, userId);
        if (response.type === "W") {
            yield put(notificationActions.showNotification(response.data.message, "error", ["debinPayments"]));
            yield put({ type: types.CANCEL_DEBIN_FAILURE });
        } else if (response.data.data?.error) {
            yield put(notificationActions.showNotification(response.data.data.message, "error", ["debinPayments"]));
            yield put({ type: types.CLEAN_DEBIN_SELLER });
        } else {
            yield put({
                type: types.LOAD_DEBIN_SELLER,
                seller: response.data.data?.seller,
                prestaciones: response.data.data?.prestaciones,
            });
        }
    } catch (response) {
        yield put(notificationActions.showNotification(response.data.data?.message, "error", ["debinPayments"]));
        yield put({ type: types.CLEAN_DEBIN_BENEFICIARY });
        yield put({ type: types.CLEAN_DEBIN_SELLER });
    } finally {
        if (setSubmitting != null) {
            setSubmitting(false);
        }
    }
}

function* loadSentRequests({ filters }) {
    const response = yield call(loadSentRequestsMiddleware, filters);
    if (response.type === "W") {
        yield put(notificationActions.showNotification(response.data.message, "error", ["debin"]));
        yield put({ type: types.LOAD_SENT_REQUESTS_FAILURE });
    } else {
        yield put({
            type: types.LOAD_SENT_REQUESTS_SUCCESS,
            sentRequests: response.data.data.sentRequests,
            totalPages: parseInt(response.data.data.totalPages, 10),
            pageNumber: filters.pageNumber,
        });
    }
}

function* sendAddAccountAction({ otp, accountToChange, userId, formikBag }) {
    const response = yield call(sendAddAccountsAction, otp, accountToChange.idProduct, userId);
    if (response.type === "W") {
        if (response.data.code === "COR020W") {
            formikBag.setErrors(response.data.data);
        } else {
            yield put(notificationActions.showNotification(response.data.message, "error", ["debin"]));
        }
        yield put({ type: types.SEND_ADD_ACCOUNT_ACTION_FAILURE });
    } else {
        yield put(routerActions.push("/charges/debin/addAccounts"));
        yield put(
            notificationActions.showNotification(i18n.get("debin.addAccount.success.message"), "success", [
                "debinAccounts",
            ]),
        );
    }
}

function* cancelDebinRequest({ debinToCancel, formikBag }) {
    const response = yield call(cancelDebin, debinToCancel);

    if (response.type === "W") {
        if (response.data.code === "COR020W") {
            const message = credentialsErrorMessage(response.data?.data) || response.data.message;
            formikBag.setErrors({ credential: message });
        } else {
            yield put(notificationActions.showNotification(response.data.message, "error", ["debinPayments"]));
        }
        yield put({ type: types.CANCEL_DEBIN_FAILURE });
    } else if (response.type === "I") {
        yield put({
            type: types.CANCEL_DEBIN_SUCCESS,
        });
        yield put(routerActions.push("/debinPayments"));
        yield put(
            notificationActions.showNotification(i18n.get("debin.cancel.success.message"), "success", [
                "debinPayments",
            ]),
        );
    }
    formikBag.setSubmitting(false);
}

function* loadIncomingDebinList({ filters }) {
    const response = yield call(loadIncomingDebinListMiddleware, filters);
    if (response.type === "W") {
        yield put(notificationActions.showNotification(response.data.message, "error", ["debin"]));
        yield put({ type: types.LOAD_INCOMING_DEBIN_LIST_FAILURE });
    } else {
        yield put({
            type: types.LOAD_INCOMING_DEBIN_LIST_SUCCESS,
            sentRequests: response.data.data.sentRequests,
            totalPages: parseInt(response.data.data.totalPages, 10),
        });
    }
}

function* removeSellerAccount({ accountToChange }) {
    const response = yield call(removeSellerAccountMiddleware, accountToChange.idProduct);

    if (response.type === "W") {
        yield put(notificationActions.showNotification(response.data.message, "error", ["debin"]));
        yield put({ type: types.REMOVE_SELLER_ACCOUNT_FAILURE });
    } else {
        yield put(routerActions.push("/charges/debin/addAccounts"));
        yield put(
            notificationActions.showNotification(i18n.get("debin.removeAccount.success.message"), "success", ["debin"]),
        );
        yield put({
            type: types.REMOVE_SELLER_ACCOUNT_SUCCESS,
        });
    }
}

function* acceptIncomingDebinPreRequest({ idDebin, isDetail }) {
    const response = yield call(acceptIncomingDebinPreMiddleware, idDebin);
    if (response.type === "W") {
        const message = credentialsErrorMessage(response.data?.data) || response.data.message;
        yield put(notificationActions.showNotification(message, "error", ["debin"]));
        yield put({ type: types.ACCEPT_INCOMING_DEBIN_PRE_FAILURE });
        if (isDetail) {
            yield put(
                notificationActions.showNotification(i18n.get("global.unexpectedError"), "error", ["debinPayments"]),
            );
            yield put(push("/debinPayments"));
        }
    } else {
        yield put({
            type: types.ACCEPT_INCOMING_DEBIN_PRE_SUCCESS,
            debinDetail: response.data.data.debinDetail,
        });
    }
}

function* acceptIncomingDebinPreRequestRedirect({ idDebin, route }) {
    const response = yield call(acceptIncomingDebinPreMiddleware, idDebin);

    if (response.type === "W") {
        const message = credentialsErrorMessage(response.data?.data) || response.data.message;
        yield put(notificationActions.showNotification(message, "error", ["debin"]));
        yield put({ type: types.ACCEPT_INCOMING_DEBIN_PRE_FAILURE });
    } else {
        yield put({
            type: types.ACCEPT_INCOMING_DEBIN_PRE_SUCCESS,
            debinDetail: response.data.data.debinDetail,
        });
    }

    yield put(push(route));
}

function* getSellerDebinDetail({ idDebin }) {
    const response = yield call(loadSellerDebinDetail, idDebin);

    if (response.type === "W") {
        yield put(notificationActions.showNotification(response.data.message, "error", ["debin"]));
        yield put({ type: types.GET_SELLER_DEBIN_DETAIL_FAILURE });
    } else {
        yield put({
            type: types.GET_SELLER_DEBIN_DETAIL_SUCCESS,
            debinDetail: response.data.data.debinDetail,
        });
    }
}

function* sendDebinCreateRequest({ newDebinRequest, formikBag, credentialType }) {
    const { userId } = sessionSelectors.getUser(store.getState());
    let response;
    try {
        if (newDebinRequest?.solicitDebin?.idTransaction) {
            response = yield call(createDebin, {
                userId,
                active: true,
                ...newDebinRequest,
                idTransaction: newDebinRequest?.solicitDebin?.idTransaction,
            });
        } else {
            response = yield call(createDebin, {
                userId,
                active: true,
                ...newDebinRequest,
            });
        }

        if (response.type === "W") {
            let message = credentialsErrorMessage(response?.data?.data) || response?.data?.message;

            if (response.data?.data?.password || response.data?.data?.otp) {
                message =
                    credentialType === "password"
                        ? i18n.get("preferences.password.password.incorrect")
                        : i18n.get("login.step1.secondFactor.invalid");
                formikBag.setErrors({
                    credential: message,
                });
            }

            yield put(
                notificationActions.showNotification(message, "error", [
                    "solicitDebinConfirmation",
                    "debinTrasaction", // scope del comprobante
                ]),
            );
            yield put({ type: types.DEBIN_SEND_FAILURE });
        } else {
            if (response.data.code === "COR000I") {
                yield put(
                    notificationActions.showNotification(i18n.get("debin.request.success"), "success", [
                        "debinTrasaction",
                    ]),
                );
            }

            yield put({
                type: types.DEBIN_SEND_SUCCESS,
                debinTicket: {
                    status: ticketStates[response.data.code],
                    ...response.data,
                },
            });
        }
    } finally {
        formikBag.setSubmitting(false);
    }
}

function* sendDebinCreatePreRequest({ expirationDate, concept, idAccount, recipients }) {
    const response = yield call(createDebinPre, { expirationDate, concept, idAccount, recipients });

    if (response.type === "W") {
        const message = credentialsErrorMessage(response?.data?.data) || response?.data?.message;
        yield put(routerActions.push("/debinPayments"));
        yield put(notificationActions.showNotification(message, "error", ["debinPayments"]));
        yield put({ type: types.SEND_DEBIN_PRE_FAILURE });
    } else if (response.data?.code === "COR000I") {
        yield put({
            type: types.SEND_DEBIN_PRE_SUCCESS,
            recipients: response.data.data.recipients,
        });
    }
}

function* sendCredinCreatePreRequest({ debinSeller, cbuList }) {
    const { concept, cbu, amount, idAccount } = debinSeller;
    const credin = {
        concept,
        cbu,
        amount,
        idAccount,
    };
    const response = yield call(createCredinPre, credin, cbuList);

    if (response.type === "W" && response.data.code !== "API516W") {
        const message = response?.data?.data?.message || response?.data?.message;
        yield put(notificationActions.showNotification(message, "error", ["debin"]));
        yield put({ type: types.SEND_CREDIN_PRE_FAILURE });
    } else if (response.data.code === "COR001E") {
        yield put({ type: types.SEND_CREDIN_PRE_FAILURE });
        yield put(notificationActions.showNotification(response.data.data.message, "error", ["debin"]));
    } else {
        yield put({
            type: types.SEND_CREDIN_PRE_SUCCESS,
            recipients: response.data.data.recipients,
            id: response.data.data.id,
        });
        yield put(routerActions.push("requestCredin/generateCredin"));
    }
}

function* sendCredinCreateRequest({ debinSeller, recipients, otp, id, formikBag }) {
    const { setSubmitting, setErrors } = formikBag;
    try {
        const credin = {
            ...debinSeller,
        };

        const cbuList = formikBag?.props?.cbuList;
        const response = yield call(createCredin, otp, credin, recipients, id, cbuList);
        if (response.type === "W" && response.data.code !== "API516W" && response.data.code !== "COR020W") {
            yield put(notificationActions.showNotification(response.data.message, "error", ["debin"]));
            yield put({ type: types.CREDIN_SEND_FAILURE });
        } else if (response.data.code === "COR999E" || response.data.code === "COR020W") {
            yield put({ type: types.CREDIN_SEND_FAILURE });
            setErrors(response.data.data);
            if (response.data.code !== "COR020W") {
                yield put(notificationActions.showNotification(response.data.data.message, "error", ["debin"]));
            }
        } else {
            yield put(formActions.readTransaction({ pathname: `/transaction/${response.data.idTransaction}` }));

            yield take("READ_TRANSACTION_REQUEST");

            yield put(debinActions.setDebinSeller({}, []));
        }
    } finally {
        setSubmitting(false);
    }
}

function* acceptIncomingDebinSendRequest({ debin, formikBag, credentialType }) {
    const { setSubmitting, setErrors } = formikBag;
    const { userId } = sessionSelectors.getUser(store.getState());
    const params = { ...debin, userId };
    gaEventTracker.triggerEvent(events.debin_pagar());
    try {
        const response = yield call(acceptIncomingDebinSendMiddleware, params);
        if (response.type === "W") {
            let message = credentialsErrorMessage(response.data?.data) || response.data?.message;
            yield put(notificationActions.showNotification(message, "error", ["debinPayments", "debinActionPay"]));

            if (response.data?.data?.password || response.data?.data?.otp) {
                message =
                    credentialType === "password"
                        ? "preferences.password.password.incorrect"
                        : "login.step1.secondFactor.invalid";
                setErrors({
                    credential: i18n.get(message),
                });
            }

            yield put({ type: types.ACCEPT_INCOMING_DEBIN_SEND_FAILURE });
        } else {
            if (response.data?.code === "COR000I") {
                yield put(
                    notificationActions.showNotification(i18n.get("accept.incoming.debin.success"), "success", [
                        "debin.ticket",
                    ]),
                );
            }

            yield put({
                type: types.ACCEPT_INCOMING_DEBIN_SEND_SUCCESS,
            });

            yield put({
                type: types.DEBIN_TICKET_SUCCESS,
                debinTicket: {
                    status: ticketStates[response.data.code],
                    ...response.data,
                },
            });
        }
    } finally {
        setSubmitting(false);
    }
}
function* declineIncomingDebinSendRequest({ debin, formikBag, credentialType }) {
    gaEventTracker.triggerEvent(events.debin_rechazar());
    const { setSubmitting, setErrors } = formikBag;
    const { userId } = sessionSelectors.getUser(store.getState());
    const params = { ...debin, userId };

    try {
        const response = yield call(declineIncomingDebinSendMiddleware, params);
        if (response.type === "W") {
            if (response.data.code === "COR020W") {
                yield put(
                    notificationActions.showNotification(response.data?.message, "error", ["/debinPayments"]),
                );
                if (response.data?.data?.password) {
                    const msj =
                        credentialType === "password"
                            ? "preferences.password.password.incorrect"
                            : "login.step1.secondFactor.invalid";
                    setErrors({
                        credential: i18n.get(msj),
                    });
                }
                if(response.data?.data?.otp) {
                     yield put(
                        notificationActions.showNotification(response.data.data.otp, "error", ["/debinPayments"]),
                    );
                }
            } else {
                yield put(notificationActions.showNotification(response.data.message, "error", ["/debinPayments"]));
            }
            yield put({ type: types.DECLINE_INCOMING_DEBIN_FAILURE });
        } else {
            yield put({
                type: types.DECLINE_INCOMING_DEBIN_SUCCESS,
            });
            if (response.data.code !== "API516W") {
                if (response.data.code === INSUFFICIENT_SIGNATURES) {
                    yield put(
                        notificationActions.showNotification(i18n.get("payments.debin.decline.pending"), "success", [
                            "debin.reject.notification",
                        ]),
                    );
                } else {
                    yield put(
                        notificationActions.showNotification(
                            i18n.get("payments.debin.decline.confirmationMessage"),
                            "success",
                            ["debin.reject.notification"],
                        ),
                    );
                }
            }
            yield put({
                type: types.DEBIN_TICKET_SUCCESS,
                debinTicket: {
                    status: ticketStates[response.data.code],
                    ...response.data,
                },
            });
        }
    } finally {
        setSubmitting(false);
    }
}
function* declineFixedTermSendRequest({ currency, amount, idDebin, otp, account, userId, formikBag }) {
    const { setSubmitting, setErrors } = formikBag;
    try {
        const response = yield call(
            declineIncomingDebinSendMiddleware,
            currency,
            amount,
            idDebin,
            otp,
            account.idProduct,
            userId,
        );

        if (response.type === "W") {
            if (response.data.code === "COR020W") {
                setErrors(response.data.data);
            } else {
                yield put(notificationActions.showNotification(response.data.message, "error", ["debin"]));
            }
            yield put({ type: types.DECLINE_INCOMING_DEBIN_FAILURE });
        } else {
            yield put({
                type: types.DECLINE_INCOMING_DEBIN_SUCCESS,
            });
            const confirmationMessage = i18n.get("payments.debin.decline.confirmationMessage");
            yield put(notificationActions.showNotification(confirmationMessage, "success", ["debin"]));
            yield put(routerActions.push("/fixedTermAcceptance"));
        }
    } finally {
        setSubmitting(false);
    }
}

function* debinPreRequest() {
    const response = yield call(listConcepts);
    if (response.type === "W") {
        yield put({ type: types.DEBIN_PRE_FAILURE });
    } else {
        yield put({
            type: types.DEBIN_PRE_SUCCESS,
            concepts: response.data.data.concepts,
            accounts: response.data.data.accounts,
        });
    }
}

function* setIsBackFromSummary({ page }) {
    yield put(routerActions.push(page));
}

function* getAccountInfoByCbuCbuCvuAlias({ creditCbu, formikBag }) {
    const { setOpenSidebarForm, selectBeneficiary } = formikBag.props;
    try {
        const response = yield call(accountInfoByCbuCvuAlias, { creditCbu });
        if (response.type === "W") {
            const message = credentialsErrorMessage(response.data?.data);

            yield put(
                notificationActions.showNotification(message || response.data?.message, "error", ["debinPayments"]),
            );
            return;
        }

        const { beneficiary } = response.data.data;

        if (isEmpty(beneficiary)) {
            yield put(
                notificationActions.showNotification("transfers.problems.finding.cbu", "error", ["debinPayments"]),
            );
            return;
        }
        const moreFileds = {
            reference: beneficiary?.name,
            email: beneficiary?.recipientEmail,
            account: beneficiary?.cbu,
            document: `${beneficiary?.recipientDocumentType} ${beneficiary?.recipientDocumentNumber}`,
            headline: beneficiary?.recipientName,
        };
        selectBeneficiary({ ...beneficiary, ...moreFileds });
    } finally {
        setOpenSidebarForm(false);
        formikBag.resetForm(false);
        formikBag.setSubmitting(false);
    }
}
function* getAccountInfoByCbuCbuCvuAliasApplicant({ creditCbu, selectBeneficiaryApplicant }) {
    const response = yield call(accountInfoByCbuCvuAlias, { creditCbu });
    if (response.type === "W") {
        yield put(notificationActions.showNotification(response.data?.message, "error", ["debinPayments"]));
        return;
    }

    const { beneficiary } = response.data.data;

    if (isEmpty(beneficiary)) {
        yield put(notificationActions.showNotification("transfers.problems.finding.cbu", "error", ["debinPayments"]));
        return;
    }
    const moreFileds = {
        reference: beneficiary?.name,
        email: beneficiary?.recipientEmail,
        account: beneficiary?.cbu,
        document: `${beneficiary?.recipientDocumentType} ${beneficiary?.recipientDocumentNumber}`,
        headline: beneficiary?.recipientName,
    };

    selectBeneficiaryApplicant({ ...beneficiary, ...moreFileds });
}

function* listCredentialsGroups() {
    const { userId: idUser } = sessionSelectors.getUser(store.getState());
    const params = { idUser, active: true, idActivityToRead: "debin.create.send" };
    const response = yield call(listCredentialsGroupsMidle, params);

    if (response && response.status === 200) {
        yield put({
            type: types.DEBIN_TICKET_CREDENTIAL_SUCCESS,
            debinRequiredCredentials: response.data.data?.groups[0]?.credentials,
        });
    }
}

function* scheduledDebinList({ formFilter }) {
    const response = yield call(scheduledDebinListMidle, formFilter);
    if (response.type === "W") {
        yield put(notificationActions.showNotification(response.data.message, "error", ["debin"]));
        yield put({
            type: types.SCHEDULED_DEBINS_LIST_FAILURE,
        });
    } else if (response.data.data?.error) {
        yield put({
            type: types.SCHEDULED_DEBINS_LIST_FAILURE,
        });
    } else {
        yield put({
            type: types.SCHEDULED_DEBINS_LIST_SUCCESS,
            scheduledDebins: response.data.data?.scheduledDebins,
        });
    }
}

function* rejectScheduledDebinSend({ params, formikBag, credentialType }) {
    try {
        const response = yield call(rejectScheduledDebinSendMid, params);

        if (response.type === "W") {
            let message;
            if (response.data?.data?.otp) {
                message = i18n.get("login.step1.secondFactor.invalid");

                formikBag.setErrors({
                    credential: message,
                });
            } else {
                message = i18n.get("global.unexpectedError");
            }

            yield put(notificationActions.showNotification(message, "error", ["debin"]));
            yield put({ type: types.REJECT_SCHEDULED_DEBIN_FAILURE });
        } else {
            yield put({
                type: types.REJECT_SCHEDULED_DEBIN_SUCCESS,
            });
            yield put(replace(`/scheduledDebin/${"reject"}/transaction/${response.data.idTransaction}`));
        }
    } finally {
        formikBag.setSubmitting(false);
    }
}

function* acceptScheduledDebinSend({ params, formikBag, credentialType }) {
    try {
        const response = yield call(acceptScheduledDebinSendMid, params);

        if (response.type === "W") {
            let message;
            if (response.data?.data?.otp) {
                message = i18n.get("login.step1.secondFactor.invalid");

                formikBag.setErrors({
                    credential: message,
                });
            } else {
                message = i18n.get("global.unexpectedError");
            }

            yield put(notificationActions.showNotification(message, "error", ["debin"]));
            yield put({ type: types.ACCEPT_SCHEDULED_DEBIN_FAILURE });
        } else {
            yield put({
                type: types.ACCEPT_SCHEDULED_DEBIN_SUCCESS,
            });
            yield put(replace(`/scheduledDebin/${"accept"}/transaction/${response.data.idTransaction}`));
        }
    } finally {
        formikBag.setSubmitting(false);
    }
}

function* pauseScheduledDebinSend({ params, formikBag, credentialType }) {
    try {
        const response = yield call(pauseScheduledDebinSendMid, params);

        if (response.type === "W") {
            let message;
            if (response.data?.data?.otp) {
                message = i18n.get("login.step1.secondFactor.invalid");

                formikBag.setErrors({
                    credential: message,
                });
            } else {
                message = i18n.get("global.unexpectedError");
            }

            yield put(notificationActions.showNotification(message, "error", ["debin"]));
            yield put({ type: types.PAUSE_SCHEDULED_DEBIN_FAILURE });
        } else {
            yield put({
                type: types.ACCEPT_SCHEDULED_DEBIN_SUCCESS,
            });
            yield put(replace(`/scheduledDebin/${"pause"}/transaction/${response.data.idTransaction}`));
        }
    } finally {
        formikBag.setSubmitting(false);
    }
}

function* cancelScheduledDebinSend({ params, formikBag, credentialType }) {
    try {
        const response = yield call(cancelScheduledDebinSendMid, params);

        if (response.type === "W") {
            let message;
            if (response.data?.data?.otp) {
                message = i18n.get("login.step1.secondFactor.invalid");

                formikBag.setErrors({
                    credential: message,
                });
            } else {
                message = i18n.get("global.unexpectedError");
            }

            yield put(notificationActions.showNotification(message, "error", ["debin"]));
            yield put({ type: types.CANCEL_SCHEDULED_DEBIN_FAILURE });
        } else {
            yield put({
                type: types.CANCEL_SCHEDULED_DEBIN_SUCCESS,
            });
            yield put(replace(`/scheduledDebin/${"cancel"}/transaction/${response.data.idTransaction}`));
        }
    } finally {
        formikBag.setSubmitting(false);
    }
}

function* activateScheduledDebinSend({ params, formikBag, credentialType }) {
    try {
        const response = yield call(activateScheduledDebinSendMid, params);

        if (response.type === "W") {
            let message;
            if (response.data?.data?.otp) {
                message = i18n.get("login.step1.secondFactor.invalid");

                formikBag.setErrors({
                    credential: message,
                });
            } else {
                message = i18n.get("global.unexpectedError");
            }

            yield put(notificationActions.showNotification(message, "error", ["debin"]));
            yield put({ type: types.ACCEPT_SCHEDULED_DEBIN_FAILURE });
        } else {
            yield put({
                type: types.ACTIVATE_SCHEDULED_DEBIN_SUCCESS,
            });
            yield put(replace(`/scheduledDebin/${"activate"}/transaction/${response.data.idTransaction}`));
        }
    } finally {
        formikBag.setSubmitting(false);
    }
}

function* chargeBackSend({ params, formikBag, credentialType }) {
    const response = yield call(chargeBackSendMiddleware, params);

    if (response.type === "W") {
        // logica de warnings
        let message = credentialsErrorMessage(response?.data?.data) || response?.data?.message;

        if (response.data?.data?.password || response.data?.data?.otp) {
            message =
                credentialType === "password"
                    ? i18n.get("preferences.password.password.incorrect")
                    : i18n.get("login.step1.secondFactor.invalid");
            formikBag.setErrors({
                credential: message,
            });
            formikBag.setSubmitting(false);
        }

        yield put(notificationActions.showNotification(message, "error", ["/debin/chargeBack"]));
    }
    yield put({
        type: types.DEBIN_TICKET_SUCCESS,
        debinTicket: {
            status: ticketStates[response.data.code],
            ...params,
            ...response.data,
        },
    });
}

function* chargeBackSendTrxPl({ params, formikBag, credentialType }) {
    const response = yield call(chargeBackTrxPlSend, params);

    if (response.type === "W") {
        let message = credentialsErrorMessage(response?.data?.data) || response?.data?.message;

        if (response.data?.data?.password || response.data?.data?.otp) {
            message =
                credentialType === "password"
                    ? i18n.get("preferences.password.password.incorrect")
                    : i18n.get("login.step1.secondFactor.invalid");
            formikBag.setErrors({
                credential: message,
            });
        }
        formikBag.setSubmitting(false);

        yield put(notificationActions.showNotification(message, "error", ["/debin/chargeBack"]));
    } else {
        yield put({
            type: types.DEBIN_TICKET_SUCCESS,
            debinTicket: {
                status: codeStates[response.data.code],
                ...params,
                ...response.data,
            },
        });
    }
}
